/*
  mb-cart-drawer.js  ·  Morabel
  Opens the theme's cart drawer after a custom add-to-cart, instead of
  sending the shopper to the cart page.

  Used by sections/mb-pdp-hero.liquid and sections/mb-refill-hero.liquid.
  Those sections add to the cart themselves (so they can add the head and
  the refill plan together), which skips the theme's own drawer code. This
  file does the two things the theme would have done: refresh the drawer's
  contents, then open it.

  window.MbCartDrawer.show() returns a promise. If it rejects for any reason
  (drawer switched off, theme changed, network), the caller goes to the cart
  page. The reason is kept in window.MbCartDrawer.lastError and logged to the
  browser console.
*/
(function () {
  'use strict';
  if (window.MbCartDrawer) return;

  var MODAL_NAME = 'cart';
  var WILL_CHANGE = 'xo-cart-will-change[xo-unique-id]';
  var SHORT_CLASS = 'xo-modal-content__inner--shot-width';
  var TIMEOUT_MS = 7000;

  function available() {
    return !!(
      window.xoModal &&
      typeof window.xoModal.open === 'function' &&
      document.querySelector('xo-modal[xo-name="' + MODAL_NAME + '"]')
    );
  }

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  /* sections that hold cart content: the drawer itself, plus any section showing cart pieces */
  function sectionIds() {
    var ids = [];
    function add(el) {
      if (!el || !el.id) return;
      var id = el.id.replace(/^shopify-section-/, '');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    }
    add(document.querySelector('.section-xo-cart-mini'));
    document.querySelectorAll(WILL_CHANGE).forEach(function (el) {
      add(el.closest('[id^="shopify-section-"]'));
    });
    return ids.slice(0, 5);
  }

  function parse(html) {
    return new DOMParser().parseFromString(String(html || ''), 'text/html');
  }

  /* Plain requests only: no "Accept: application/json" header. With that header
     Shopify answers a product URL with the product's data, not the sections. */
  function request(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error('HTTP ' + res.status + ' from ' + url);
      return res;
    });
  }

  /* fresh copies of the cart sections, rendered by Shopify for the cart as it is now */
  function sectionDocs(base, ids) {
    var url = base + (base.indexOf('?') === -1 ? '?' : '&') + 'sections=' + ids.map(encodeURIComponent).join(',');
    return request(url).then(function (res) { return res.json(); }).then(function (map) {
      var docs = ids
        .filter(function (id) { return map && typeof map[id] === 'string'; })
        .map(function (id) { return parse(map[id]); });
      if (!docs.length) throw new Error('No cart sections in the response from ' + base);
      return docs;
    });
  }

  /* last resort: re-read the whole page */
  function pageDocs() {
    return request(window.location.href)
      .then(function (res) { return res.text(); })
      .then(function (html) { return [parse(html)]; });
  }

  function freshDocs() {
    var ids = sectionIds();
    if (!ids.length) return pageDocs();
    return sectionDocs(window.location.pathname, ids)
      .catch(function () { return sectionDocs(cartUrl(), ids); })
      .catch(pageDocs);
  }

  /* copy the fresh cart pieces over the ones on the page; returns how many were updated */
  function apply(docs) {
    var live = Array.prototype.slice.call(document.querySelectorAll(WILL_CHANGE));
    var updated = 0;
    docs.forEach(function (doc) {
      doc.querySelectorAll(WILL_CHANGE).forEach(function (fresh) {
        var uid = fresh.getAttribute('xo-unique-id');
        live.forEach(function (el) {
          if (el.getAttribute('xo-unique-id') !== uid) return;
          Array.prototype.forEach.call(fresh.attributes, function (attr) {
            if (attr.name !== 'class' && attr.name !== 'style') el.setAttribute(attr.name, attr.value);
          });
          el.innerHTML = fresh.innerHTML;
          updated += 1;
        });
      });

      /* the drawer is narrower while the cart is empty */
      var freshInner = doc.querySelector('#xo-cart-mini .xo-modal-content__inner');
      var liveInner = document.querySelector('#xo-cart-mini .xo-modal-content__inner');
      if (freshInner && liveInner) {
        liveInner.classList.toggle(SHORT_CLASS, freshInner.classList.contains(SHORT_CLASS));
      }
    });
    return updated;
  }

  /* cart count bubble in the header */
  function updateCount() {
    var bubbles = document.querySelectorAll('xo-cart-size');
    if (!bubbles.length) return Promise.resolve();
    return request(cartUrl() + '.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        if (!cart || typeof cart.item_count !== 'number') return;
        bubbles.forEach(function (el) { el.textContent = String(cart.item_count); });
      })
      .catch(function () { /* the count is cosmetic; never block the drawer on it */ });
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve, reject) {
      var timer = window.setTimeout(function () { reject(new Error('Cart drawer timed out')); }, ms);
      promise.then(
        function (value) { window.clearTimeout(timer); resolve(value); },
        function (err) { window.clearTimeout(timer); reject(err); }
      );
    });
  }

  var api = { available: available, show: show, lastError: null };

  function show() {
    var work;
    try {
      if (!available()) throw new Error('Cart drawer is not available');
      work = withTimeout(
        Promise.all([freshDocs(), updateCount()]).then(function (results) {
          if (!apply(results[0])) throw new Error('Cart drawer contents not found');
          window.xoModal.open(MODAL_NAME);
          api.lastError = null;
        }),
        TIMEOUT_MS
      );
    } catch (err) {
      work = Promise.reject(err);
    }
    return work.catch(function (err) {
      api.lastError = (err && err.message) || String(err);
      if (window.console && console.warn) console.warn('[Morabel cart drawer] ' + api.lastError);
      throw err;
    });
  }

  window.MbCartDrawer = api;
})();